/**
 * 인증 fetch — 401 응답 시 세션 refresh 후 1회 재시도
 */

import { API_REQUEST_TIMEOUT_MS } from '../config/api';
import {
  clearStoredAuth,
  getAuthHeaders,
  getJWTToken,
  getSessionId,
  getTenantId,
  setJWTToken,
} from '../utils/jwtUtils';
import { refreshSession } from './authApi';
import {
  BUSINESS_PLANE_BLOCKED_CODE,
  clearAuthAndRedirectForBusinessPlaneBlock,
} from './businessPlaneAuth';
import { logger } from '@shared/logger';

const AUTH_REFRESH_PATH = '/api/system/auth/refresh';
const AUTH_LOGIN_PATH = '/api/system/auth/login';

let refreshInFlight: Promise<boolean> | null = null;

function fetchWithTimeout(url: string, init?: RequestInit): Promise<Response> {
  return fetch(url, {
    ...init,
    signal: init?.signal ?? AbortSignal.timeout(API_REQUEST_TIMEOUT_MS),
  });
}

function isAuthEndpoint(url: string): boolean {
  return url.includes(AUTH_REFRESH_PATH) || url.includes(AUTH_LOGIN_PATH);
}

async function performTokenRefresh(): Promise<boolean> {
  const sessionId = getSessionId();
  if (!sessionId) return false;

  const tenantId = getTenantId() ?? undefined;
  const result = await refreshSession(sessionId, tenantId);
  if (!result.success || !result.accessToken) return false;

  setJWTToken(result.accessToken);
  return true;
}

export function clearAuthAndRedirectToLogin(): void {
  clearStoredAuth();
  if (typeof window !== 'undefined' && !window.location.pathname.startsWith('/login')) {
    window.location.assign('/login');
  }
}

function hasStoredAuthSession(): boolean {
  return Boolean(getSessionId() || getJWTToken());
}

async function readApiErrorCode(response: Response): Promise<string | undefined> {
  try {
    const body = (await response.clone().json()) as { code?: string };
    return typeof body.code === 'string' ? body.code : undefined;
  } catch {
    return undefined;
  }
}

async function handleBusinessPlaneBlocked(response: Response): Promise<Response> {
  const code = await readApiErrorCode(response);
  if (code !== BUSINESS_PLANE_BLOCKED_CODE) return response;

  clearAuthAndRedirectForBusinessPlaneBlock();
  return response;
}

export async function fetchWithAuth(url: string, init: RequestInit = {}): Promise<Response> {
  const hadAuthSession = hasStoredAuthSession();

  const buildHeaders = (): Record<string, string> => ({
    ...getAuthHeaders(),
    ...((init.headers as Record<string, string> | undefined) ?? {}),
  });

  const doFetch = () =>
    fetchWithTimeout(url, {
      ...init,
      headers: buildHeaders(),
    });

  const response = await doFetch();

  if (response.status === 403) {
    return handleBusinessPlaneBlocked(response);
  }

  if (response.status !== 401 || isAuthEndpoint(url) || !hadAuthSession) {
    return response;
  }

  if (!refreshInFlight) {
    refreshInFlight = performTokenRefresh().finally(() => {
      refreshInFlight = null;
    });
  }

  const refreshed = await refreshInFlight;
  if (!refreshed) {
    logger.warn('Token refresh failed; clearing auth session');
    clearAuthAndRedirectToLogin();
    return response;
  }

  return doFetch();
}
